import React, { useState } from 'react';

type Priority = 'High'|'Medium'|'Low';
interface Task { id:number; title:string; due:string; priority:Priority; assignee:string; done:boolean; }

const MOCK: Task[] = [
  {id:1,title:'Follow up with Rivera Family re: whole life quote',due:'2026-04-14',priority:'High',assignee:'Shawn',done:false},
  {id:2,title:'Send renewal reminder — Johnson Home Policy',due:'2026-04-15',priority:'High',assignee:'Teresa',done:false},
  {id:3,title:'Review ABC Logistics commercial auto quote',due:'2026-04-14',priority:'Medium',assignee:'John',done:false},
  {id:4,title:'Update contact record — David Park new address',due:'2026-04-16',priority:'Low',assignee:'Shawn',done:true},
  {id:5,title:'Schedule appointment — Southeast Catering',due:'2026-04-17',priority:'Medium',assignee:'Teresa',done:false},
];

const pColor: Record<Priority,string> = { High:'bg-red-100 text-red-700', Medium:'bg-yellow-100 text-yellow-700', Low:'bg-gray-100 text-gray-600' };

const TaskManagement: React.FC = () => {
  const [tasks, setTasks] = useState<Task[]>(MOCK);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<'All'|'Active'|'Done'>('All');
  const [newTask, setNewTask] = useState({ title:'', due:'', priority:'Medium' as Priority, assignee:'Shawn' });

  const filtered = tasks.filter(t => filter==='All' || (filter==='Active'&&!t.done) || (filter==='Done'&&t.done));
  const toggle = (id:number) => setTasks(p=>p.map(t=>t.id===id?{...t,done:!t.done}:t));
  const del = (id:number) => setTasks(p=>p.filter(t=>t.id!==id));
  const add = () => {
    if (!newTask.title) return;
    setTasks(p=>[...p,{...newTask,id:Date.now(),done:false}]);
    setNewTask({title:'',due:'',priority:'Medium',assignee:'Shawn'});
    setShowModal(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">{tasks.filter(t=>!t.done).length} pending · {tasks.filter(t=>t.done).length} completed</p>
        </div>
        <button onClick={()=>setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ New Task</button>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {(['All','Active','Done'] as const).map(f=>(
          <button key={f} onClick={()=>setFilter(f)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter===f?'bg-white shadow text-gray-900':'text-gray-600 hover:text-gray-900'}`}>{f}</button>
        ))}
      </div>
      <div className="space-y-2">
        {filtered.map(t => (
          <div key={t.id} className={`bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-3 ${t.done?'opacity-60':''}`}>
            <button onClick={()=>toggle(t.id)} className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${t.done?'bg-green-500 border-green-500':'border-gray-400 hover:border-green-400'}`}>
              {t.done && <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>}
            </button>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${t.done?'line-through text-gray-400':'text-gray-900'}`}>{t.title}</p>
              <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${pColor[t.priority]}`}>{t.priority}</span>
                <span className="text-xs text-gray-500">Due: {new Date(t.due).toLocaleDateString()}</span>
                <span className="text-xs text-gray-500">→ {t.assignee}</span>
              </div>
            </div>
            <button onClick={()=>del(t.id)} className="text-xs text-red-400 hover:text-red-600 flex-shrink-0">✕</button>
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">New Task</h2>
            <div className="space-y-3">
              <input placeholder="Task title" value={newTask.title} onChange={e=>setNewTask(p=>({...p,title:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input type="date" value={newTask.due} onChange={e=>setNewTask(p=>({...p,due:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select value={newTask.priority} onChange={e=>setNewTask(p=>({...p,priority:e.target.value as Priority}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option>High</option><option>Medium</option><option>Low</option>
              </select>
              <select value={newTask.assignee} onChange={e=>setNewTask(p=>({...p,assignee:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                {['Shawn','Teresa','John','Amir'].map(n=><option key={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={add} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Add Task</button>
              <button onClick={()=>setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default TaskManagement;
